import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import { ThemeProvider } from './context/ThemeContext'
import { AuthProvider } from './context/AuthContext'
import AppRouter from './routes/AppRouter'
import ErrorBoundary from './components/ErrorBoundary'
import OfflineBanner from './components/OfflineBanner'
import { PwaUpdatePrompt, PwaInstallBanner } from './components/PwaPrompts'

// Recover from stale-deployment chunk errors (hashed filenames change on redeploy).
// Reload once; if it still fails after reload, let Sentry catch it normally.
window.addEventListener("error", (e) => {
  const msg = e.message ?? "";
  if (msg.includes("Failed to fetch dynamically imported module") || msg.includes("ChunkLoadError")) {
    const key = "chunk_reload_attempted";
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      window.location.reload();
    }
  }
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,

  integrations: [
    // Tracks page navigations as transactions
    Sentry.browserTracingIntegration(),
    // Records a video-like replay of the user's session when an error occurs
    Sentry.replayIntegration({
      // Don't record text/input values (PII protection)
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],

  // 10% of normal page loads are traced for performance
  tracesSampleRate: 0.1,

  // 100% of sessions that have an error get a replay recording
  replaysOnErrorSampleRate: 1.0,

  // 5% of normal sessions get a replay recording
  replaysSessionSampleRate: 0.05,

  environment: import.meta.env.MODE, // "development" or "production"

  // Don't send errors in local dev — only in production
  enabled: import.meta.env.PROD,

  beforeSend(event) {
    // Strip any M-Pesa or phone data from error payloads
    if (event.request?.data) {
      const d = event.request.data as Record<string, unknown>;
      delete d.customer_phone;
      delete d.mpesa_ref;
      delete d.phone;
    }
    return event;
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <OfflineBanner />
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
          <PwaUpdatePrompt />
      <PwaInstallBanner />
      </ThemeProvider>
    
    </ErrorBoundary>
  </StrictMode>,
)
